/* External */
import { memo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  hasFocusedChild as isFocusWithin,
  isFocusableRegistered,
  withFocusable,
} from '@noriginmedia/react-spatial-navigation';

/* Components */
import ScrollView from '../ScrollView';

/* Utils */
import { useOnBackPress } from '../../utils/keyDownEvents';
import { PageType } from '../../utils/constants';
import { COMPONENTS } from '../../utils/focusKey';

function getFocusableItemInMain() {
  const focusableItemInMain = sessionStorage.getItem('focusableItemInMain');
  if (focusableItemInMain) {
    sessionStorage.removeItem('focusableItemInMain');
    return focusableItemInMain;
  }
  return undefined;
}

function Main({
  className = '',
  openSidebar,
  children = [],
  isSidebarExpanded = false,
  pageId,
  initialScrollPosition = 0,
  hasFocusedChild,
  setFocus,
  realFocusKey,
  isModalShown,
}) {
  const handleScroll = useCallback((offset) => {
    if (
      pageId === PageType.LIVE || pageId === PageType.SPORTS
    ) {
      sessionStorage.setItem('offsetFromStartedPage', offset);
    }
  }, [pageId]);

  useOnBackPress({ listener: openSidebar, when: !isSidebarExpanded });

  useEffect(() => {
    /* `hasFocusedChild` lags one render: when a child restores focus in the
       same commit (the EPG's ScheduleCard after Back from a PDP/player), this
       parent effect runs right after it with a stale `false` and used to pull
       focus onto MAIN's first child — an empty, zero-height lander row — so
       nothing was focused and the restore never re-ran. Ask the navigation
       service, which is updated synchronously by setFocus. */
    if (!isSidebarExpanded && !hasFocusedChild && !isModalShown && !isFocusWithin(realFocusKey)) {
      /* Returning to the old EPG (Back from the player / a PDP): the grid
         restores the selected card itself, but its rows mount a commit or
         two after MAIN, so this fallback used to run first. Focusing MAIN
         descends into the first row, the hero carousel, whose
         ScrollIntoViewIfFocused scrolls the page back to the top; the card
         then takes focus below the fold and nothing scrolls down again. Park
         focus on the EPG container instead: nothing scrolls, the restore
         steals focus when its card mounts, and if it never does, the lib's
         parked-focus completion lands on the first card that registers. */
      const restoreRow = Number.parseFloat(sessionStorage.getItem('initialSectionIndexEpg'));
      const isEpgRestorePending = restoreRow > 0
        && isFocusableRegistered(COMPONENTS.EPG)
        && !!sessionStorage.getItem('initialScheduleEpg')?.startsWith(`${restoreRow}:`);
      setFocus(getFocusableItemInMain() ?? (isEpgRestorePending ? COMPONENTS.EPG : undefined));
    }
  }, [isSidebarExpanded, hasFocusedChild, setFocus, realFocusKey, isModalShown]);

  return (
    <ScrollView
      className={className}
      initialScrollPosition={initialScrollPosition}
      onScroll={handleScroll}
    >
      {children}
    </ScrollView>
  );
}

Main.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  openSidebar: PropTypes.func.isRequired,
  isSidebarExpanded: PropTypes.bool,
  pageId: PropTypes.string,
  initialScrollPosition: PropTypes.number,
  hasFocusedChild: PropTypes.bool.isRequired,
  setFocus: PropTypes.func.isRequired,
  realFocusKey: PropTypes.string.isRequired,
  isModalShown: PropTypes.bool.isRequired,
};

export default memo(withFocusable({ trackChildren: true })(memo(Main)));
